import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from 'react';

export const LONG_PRESS_DELAY_MS = 400;

const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

/**
 * Pointer hold that fires once after `LONG_PRESS_DELAY_MS`, unless the pointer
 * moves more than `LONG_PRESS_MOVE_TOLERANCE_PX` or is released earlier.
 *
 * The pointer is captured when the hold starts, so that a mouse dragged off the
 * element keeps delivering move / up events and still cancels the hold.
 * Only the pointer that started the hold is tracked: other touches are ignored.
 */
export const useLongPress = (
  onLongPress: () => void
): {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: (event: ReactPointerEvent) => void;
  onLostPointerCapture: (event: ReactPointerEvent) => void;
} => {
  const onLongPressRef = useRef(onLongPress);
  const timerRef = useRef<number | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);

  onLongPressRef.current = onLongPress;

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    originRef.current = null;
    pointerIdRef.current = null;
  }, [clearTimer]);

  useEffect(() => reset, [reset]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      // A second touch while a hold is pending must not restart it
      if (event.button !== 0 || timerRef.current != null) {
        return;
      }

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // The pointer may already be released: the hold still works without capture
      }

      pointerIdRef.current = event.pointerId;
      originRef.current = { x: event.clientX, y: event.clientY };
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        originRef.current = null;
        onLongPressRef.current();
      }, LONG_PRESS_DELAY_MS);
    },
    [clearTimer]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      if (!originRef.current || event.pointerId !== pointerIdRef.current) {
        return;
      }

      const deltaX = event.clientX - originRef.current.x;
      const deltaY = event.clientY - originRef.current.y;

      if (deltaX * deltaX + deltaY * deltaY > LONG_PRESS_MOVE_TOLERANCE_PX ** 2) {
        reset();
      }
    },
    [reset]
  );

  const onPointerEnd = useCallback(
    (event: ReactPointerEvent) => {
      if (event.pointerId === pointerIdRef.current) {
        reset();
      }
    },
    [reset]
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: onPointerEnd,
    onPointerCancel: onPointerEnd,
    onLostPointerCapture: onPointerEnd,
  };
};
